import { useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";
import { FileSignature, Inbox, KeyRound, LockKeyhole } from "lucide-react";
import { bugReportApi, getDevToken, setDevToken } from "./api";
import { ToastProvider } from "./ui";
import { LicensesPage } from "./pages/LicensesPage";
import { TicketsPage } from "./pages/TicketsPage";
import { SignPage } from "./pages/SignPage";

const VERSION = "0.4.0";

/** Brand mark — same gradient heart as the favicon, drawn once. */
function HeartMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden>
      <defs>
        <linearGradient id="hm" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FF8A96" />
          <stop offset="55%" stopColor="#E35D6B" />
          <stop offset="100%" stopColor="#B23F50" />
        </linearGradient>
      </defs>
      <path
        fill="url(#hm)"
        d="M16 28C8.5 23.3 3 18.7 3 13.2 3 8.8 6.4 5.4 10.8 5.4c2.3 0 4.3 1.1 5.2 2.8 0.9-1.7 2.9-2.8 5.2-2.8 4.4 0 7.8 3.4 7.8 7.8 0 5.5-5.5 10.1-13 14.8Z"
      />
    </svg>
  );
}

/** Icon-rail nav item; the active state is a soft ink plate + rose icon. */
function RailLink({
  to,
  label,
  children,
}: {
  to: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      title={label}
      className={({ isActive }) =>
        `relative flex h-10 w-10 items-center justify-center rounded-xl transition ${
          isActive ? "bg-ink-700 text-rose-400" : "text-fg-faint hover:bg-ink-800 hover:text-fg-mute"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

/** Full-screen gate shown until a working admin token is stored. */
function TokenGate({ onDone }: { onDone: () => void }) {
  const [token, setToken] = useState("");
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="card rise-in w-full max-w-sm p-7">
        <HeartMark className="h-9 w-9" />
        <h1 className="mt-4 text-xl font-semibold">Heartline Ops</h1>
        <p className="mt-1 text-sm text-fg-mute">
          Paste the worker&rsquo;s <span className="font-mono text-[13px]">ADMIN_TOKEN</span> to
          unlock the console. Stored locally in this browser only.
        </p>
        <input
          className="field mt-4 font-mono text-[13px]"
          type="password"
          placeholder="admin token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && token.trim() && (setDevToken(token.trim()), onDone())}
          autoFocus
        />
        <button
          className="btn-primary mt-3 w-full"
          disabled={!token.trim()}
          onClick={() => {
            setDevToken(token.trim());
            onDone();
          }}
        >
          <LockKeyhole className="h-4 w-4" /> Unlock
        </button>
      </div>
    </div>
  );
}

export function App() {
  // Token gate: rechecked when a 401 bubbles up from any page (via the
  // `hl-unauthorized` event api.ts dispatches) or after unlock.
  const [hasToken, setHasToken] = useState<boolean>(() => Boolean(getDevToken()));
  useEffect(() => {
    const onUnauthorized = () => {
      setDevToken("");
      setHasToken(false);
    };
    window.addEventListener("hl-unauthorized", onUnauthorized);
    return () => window.removeEventListener("hl-unauthorized", onUnauthorized);
  }, []);

  // Attention dot on the Tickets rail icon — polled quietly so a fresh report
  // pings even while you're looking at Licenses.
  const [attention, setAttention] = useState(0);
  const pollAttention = useCallback(async () => {
    try {
      const r = await bugReportApi.list();
      setAttention(r.reports.filter((b) => b.unread || b.status === "open").length);
    } catch {
      /* unauth or offline — leave the dot as-is */
    }
  }, []);
  useEffect(() => {
    if (!hasToken) return;
    pollAttention();
    const t = setInterval(pollAttention, 60_000);
    return () => clearInterval(t);
  }, [hasToken, pollAttention]);

  return (
    <ToastProvider>
      {!hasToken ? (
        <TokenGate onDone={() => setHasToken(true)} />
      ) : (
        <div className="flex h-full">
          {/* Icon rail */}
          <aside className="flex w-16 shrink-0 flex-col items-center gap-2 py-4">
            <HeartMark className="mb-3 h-8 w-8" />
            <RailLink to="/licenses" label="Licenses">
              <KeyRound className="h-[18px] w-[18px]" />
            </RailLink>
            <RailLink to="/tickets" label="Tickets">
              <span className="relative">
                <Inbox className="h-[18px] w-[18px]" />
                {attention > 0 && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-rose-500" />
                )}
              </span>
            </RailLink>
            <RailLink to="/sign" label="Sign a build">
              <FileSignature className="h-[18px] w-[18px]" />
            </RailLink>
            <div className="mt-auto flex flex-col items-center gap-3">
              <button
                className="text-[10px] text-fg-faint transition hover:text-fg-mute"
                title="Forget the stored admin token"
                onClick={() => {
                  setDevToken("");
                  setHasToken(false);
                }}
              >
                lock
              </button>
              <span className="font-mono text-[10px] text-fg-faint">v{VERSION}</span>
            </div>
          </aside>

          {/* Content */}
          <main className="min-w-0 flex-1 overflow-y-auto">
            <div className="mx-auto max-w-5xl px-8 py-10">
              <Routes>
                <Route path="/" element={<Navigate to="/licenses" replace />} />
                <Route path="/licenses" element={<LicensesPage />} />
                <Route path="/tickets" element={<TicketsPage onAttentionChange={pollAttention} />} />
                <Route path="/sign" element={<SignPage />} />
                <Route path="*" element={<Navigate to="/licenses" replace />} />
              </Routes>
            </div>
          </main>
        </div>
      )}
    </ToastProvider>
  );
}
